import { useEffect, useMemo, useState } from 'react';
import {
  isChatMessageDisplayable,
  type ChatMessage,
} from '@teo/widget-shared';
import {
  getWidgetSession,
  LAUNCHER_ACK_GREETINGS_EVENT,
  normalizeLauncherPhraseText,
  readLauncherAckGreetingPhrases,
  readLauncherReadMessageIds,
  writeLauncherReadMessageIds,
} from '@teo/widget-core';
import type { HistoryStatus } from './useLoadThreadHistory.js';

/**
 * Incoming for launcher badge = bot/manager rows the user would actually see
 * in MessageList (same displayable filter).
 */
export function isIncomingForLauncherBadge(message: ChatMessage): boolean {
  return (
    !message.isTypingPlaceholder &&
    (message.role === 'bot' || message.role === 'manager') &&
    isChatMessageDisplayable(message)
  );
}

export function listIncomingForLauncherBadge(
  messages: ChatMessage[],
): ChatMessage[] {
  return messages.filter(isIncomingForLauncherBadge);
}

export function collectIncomingMessageKeys(message: ChatMessage): string[] {
  const keys = [message.id];
  if (message.clientId && message.clientId !== message.id) {
    keys.push(message.clientId);
  }
  return keys;
}

export function isIncomingMessageRead(
  message: ChatMessage,
  readIds: ReadonlySet<string>,
): boolean {
  return collectIncomingMessageKeys(message).some((key) => readIds.has(key));
}

export function messageMatchesAckedGreeting(
  message: ChatMessage,
  ackPhrases: ReadonlySet<string>,
): boolean {
  if (ackPhrases.size === 0) return false;
  const text = normalizeLauncherPhraseText(message.text ?? '');
  return text.length > 0 && ackPhrases.has(text);
}

export function countUnreadIncomingMessages(
  messages: ChatMessage[],
  readIds: ReadonlySet<string>,
  ackPhrases: ReadonlySet<string> = new Set(),
): number {
  return listIncomingForLauncherBadge(messages).filter(
    (message) =>
      !isIncomingMessageRead(message, readIds) &&
      !messageMatchesAckedGreeting(message, ackPhrases),
  ).length;
}

export function buildReadIdSetFromMessages(
  messages: ChatMessage[],
  previous?: ReadonlySet<string>,
): Set<string> {
  const next = new Set(previous ?? []);
  for (const message of listIncomingForLauncherBadge(messages)) {
    for (const key of collectIncomingMessageKeys(message)) {
      next.add(key);
    }
  }
  return next;
}

export function countIncomingForLauncherBadge(messages: ChatMessage[]): number {
  return listIncomingForLauncherBadge(messages).length;
}

export function isHistoryReadyForLauncherUnread(
  historyStatus: HistoryStatus,
  options: { loadThreadHistory: boolean; apiBase?: string },
): boolean {
  if (!options.loadThreadHistory || !options.apiBase) return true;
  return historyStatus === 'ready' || historyStatus === 'error';
}

function isRealThreadId(value: number | string | null | undefined): boolean {
  return value != null && Number(value) > 0;
}

function readAckPhraseSet(slug: string | null | undefined): Set<string> {
  if (!slug) return new Set();
  return new Set(readLauncherAckGreetingPhrases(slug));
}

type UseLauncherUnreadOptions = {
  messages: ChatMessage[];
  chatOpen: boolean;
  historyStatus: HistoryStatus;
  loadThreadHistory: boolean;
  apiBase?: string;
  threadId?: number | string | null;
  /** Storybook / controlled shell — skips live tracking */
  controlledUnread?: number;
};

/**
 * Unread badge on the launcher while chat is closed.
 * Tracks read message ids (sessionStorage). Committed greeting phrases are
 * acknowledged so backend inject of the same text does not inflate the badge.
 */
export function useLauncherUnread({
  messages,
  chatOpen,
  historyStatus,
  loadThreadHistory,
  apiBase,
  threadId,
  controlledUnread,
}: UseLauncherUnreadOptions): number {
  const [readIds, setReadIds] = useState<Set<string> | null>(null);
  const [readThreadId, setReadThreadId] = useState(threadId);
  const [ackEpoch, setAckEpoch] = useState(0);

  if (readThreadId !== threadId) {
    setReadThreadId(threadId);
    setReadIds(null);
  }

  const historyReady = isHistoryReadyForLauncherUnread(historyStatus, {
    loadThreadHistory,
    apiBase,
  });

  const ackPhrases = useMemo(() => {
    void ackEpoch;
    return readAckPhraseSet(getWidgetSession()?.slug);
  }, [ackEpoch, messages]);

  const unreadCount = useMemo(() => {
    if (readIds == null) return 0;
    return countUnreadIncomingMessages(messages, readIds, ackPhrases);
  }, [ackPhrases, messages, readIds]);

  const persistReadIds = (ids: Set<string>) => {
    const slug = getWidgetSession()?.slug;
    if (!slug || !isRealThreadId(threadId)) return;
    writeLauncherReadMessageIds(slug, threadId!, ids);
  };

  useEffect(() => {
    const onAck = () => setAckEpoch((value) => value + 1);
    window.addEventListener(LAUNCHER_ACK_GREETINGS_EVENT, onAck);
    return () => window.removeEventListener(LAUNCHER_ACK_GREETINGS_EVENT, onAck);
  }, []);

  // Seed read set once history is ready: restore from storage, or mark all
  // current incoming as read (no badge on first paint after prefetch).
  useEffect(() => {
    if (!historyReady || readIds != null) return;

    const slug = getWidgetSession()?.slug;
    if (slug && isRealThreadId(threadId)) {
      const stored = readLauncherReadMessageIds(slug, threadId!);
      if (stored.length > 0) {
        setReadIds(new Set(stored));
        return;
      }
    }

    const seeded = buildReadIdSetFromMessages(messages);
    setReadIds(seeded);
    persistReadIds(seeded);
  }, [historyReady, messages, readIds, threadId]);

  // While chat is open, everything currently visible counts as read.
  useEffect(() => {
    if (!chatOpen || !historyReady || readIds == null) return;
    const next = buildReadIdSetFromMessages(messages, readIds);
    if (next.size === readIds.size) {
      let changed = false;
      for (const id of next) {
        if (!readIds.has(id)) {
          changed = true;
          break;
        }
      }
      if (!changed) return;
    }
    setReadIds(next);
    persistReadIds(next);
  }, [chatOpen, historyReady, messages, readIds, threadId]);

  // When backend injects committed greeting phrases, fold their ids into read.
  useEffect(() => {
    if (!historyReady || readIds == null || chatOpen) return;
    if (ackPhrases.size === 0) return;

    let changed = false;
    const next = new Set(readIds);
    for (const message of listIncomingForLauncherBadge(messages)) {
      if (!messageMatchesAckedGreeting(message, ackPhrases)) continue;
      if (isIncomingMessageRead(message, next)) continue;
      for (const key of collectIncomingMessageKeys(message)) {
        next.add(key);
      }
      changed = true;
    }
    if (!changed) return;
    setReadIds(next);
    persistReadIds(next);
  }, [ackPhrases, chatOpen, historyReady, messages, readIds, threadId]);

  if (controlledUnread !== undefined) {
    return controlledUnread;
  }
  if (!historyReady || readIds == null || chatOpen) {
    return 0;
  }
  return unreadCount;
}
